import { useCallback } from 'react';
import { toast } from 'react-toastify';

import { useStore } from 'zustand';
import userStore from '@/store/modules/userStore';

import { requestNicepayAuth, type NicepayParams } from '@/utils/nicepayAuth';
import makeErrorString from '@/utils/makeErrorString';
import { updateUserInfo } from '@/services/user.service';

import { useInitPaymentMethodRegistration } from '@/hooks/queries/subscription/useInitPaymentMethodRegistration';
import { useRegisterPaymentMethod } from '@/hooks/queries/subscription/useRegisterPaymentMethod';
import type { NicePayAuthParams, PaymentMethodDto } from '@/types/subscription';

import { promptBuyerInfo, type BuyerInfo } from './promptBuyerInfo';

interface StartOptions {
    /** 등록 후 기본 결제 수단으로 설정할지 (default: true) */
    isDefault?: boolean;
    /** 등록 완료 후 콜백 (PaymentMethodDto 전달) */
    onComplete?: (method: PaymentMethodDto) => void;
}

/**
 * NicePay 빌키 등록 통합 흐름.
 *
 *   0. 사용자 정보(이름/이메일/연락처) 비어 있으면 dialog로 입력 받고 백엔드 patch
 *   1. useInitPaymentMethodRegistration  — 서버에서 SignData 포함한 NicePay 파라미터 발급
 *   2. requestNicepayAuth                — NicePay 인증창 호출 (콜백 등록)
 *   3. useRegisterPaymentMethod          — 콜백 결과(TxTid/AuthToken)를 서버에 전달해 빌키 발급/저장
 */
export function useNicePayBillingFlow() {
    const user = useStore(userStore);
    const init = useInitPaymentMethodRegistration();
    const register = useRegisterPaymentMethod();

    const start = useCallback(
        async (opts?: StartOptions) => {
            try {
                console.log({ '@@@@@user': user });
                let buyer: BuyerInfo = {
                    buyerName: user.nickname || '',
                    buyerEmail: user.email || '',
                    buyerTel: user.phone_number || '',
                };

                // 정보 누락 시 dialog로 입력 받고 백엔드/store 동기화
                const isMissing = !buyer.buyerName || !buyer.buyerEmail || !buyer.buyerTel;
                if (isMissing) {
                    const filled = await promptBuyerInfo(buyer);
                    if (!filled) return; // 사용자 취소

                    // 변경된 필드만 백엔드에 patch
                    const patch: Parameters<typeof updateUserInfo>[0] = {};
                    if (filled.buyerTel && filled.buyerTel !== user.phone_number) {
                        patch.phoneNumber = filled.buyerTel;
                    }
                    if (filled.buyerEmail && filled.buyerEmail !== user.email) {
                        patch.email = filled.buyerEmail;
                    }
                    if (filled.buyerName && filled.buyerName !== user.nickname) {
                        patch.nickname = filled.buyerName;
                    }
                    if (Object.keys(patch).length > 0) {
                        try {
                            await updateUserInfo(patch);
                            // userStore 동기화
                            userStore.getState().patchUserInfo({
                                phone_number: patch.phoneNumber,
                                email: patch.email,
                                nickname: patch.nickname,
                            });
                        } catch (e) {
                            toast.error(makeErrorString(e, '연락처 정보를 저장하지 못했어요'));
                            return;
                        }
                    }
                    buyer = filled;
                }

                const params: NicePayAuthParams = await init.mutateAsync(buyer);

                await requestNicepayAuth(params as unknown as NicepayParams, {
                    onSuccess: async (data: any) => {
                        try {
                            const authResponse = {
                                TxTid: String(data.TxTid ?? data.tid ?? ''),
                                AuthToken: String(data.AuthToken ?? data.authToken ?? ''),
                                MID: String(data.MID ?? params.MID),
                                Moid: String(data.Moid ?? params.Moid),
                            };
                            const method = await register.mutateAsync({
                                authResponse,
                                isDefault: opts?.isDefault ?? true,
                            });
                            toast.success('결제 카드를 등록했어요');
                            opts?.onComplete?.(method);
                        } catch (e) {
                            toast.error(makeErrorString(e, '카드 등록에 실패했어요'));
                        }
                    },
                    onError: (err: any) => {
                        toast.error(makeErrorString(err, '카드 등록에 실패했어요'));
                    },
                });
            } catch (e) {
                toast.error(makeErrorString(e, '카드 등록을 시작할 수 없어요'));
            }
        },
        [user, init, register],
    );

    return {
        start,
        isPending: init.isPending || register.isPending,
    };
}
